import FirebaseUploader from "../FireBaseUploader/FirebaseUploader";
import "./ConfirmModal.scss";
// MODIFICATION: Imported useRef
import { useState, useEffect, useRef } from "react";
import { useToast } from "../../context/ToastContext";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { useLoading } from "../../context/LoadingContext";
import { getInvoicesByID } from "../../services/api/InvoicesApi";

import ReactDOM from "react-dom/client";
import { ReceiptPDFTemplate } from "../../pages/RecieptPDFTemplate/ReceiptPDFTemplate";
import { formatDateTime } from "../TruncateParties";
import { useNavigate } from "react-router-dom";

const ConfirmationModal = ({
  isOpen,
  message,
  onConfirm,
  onCancel,
  options = [],
  invoiceId,
  currentStatus = "",
  totalAmount = 0,
  previousPayments = [],
  submitPaymentDetails,
  activePage,
  onClose,
  cancelledRem,
  handleDownloadReceipt: externalHandleDownloadReceipt
}) => {
  // MODIFICATION: Create a ref for the modal content
  const modalRef = useRef(null);
  const navigate = useNavigate();

  const [selectedValue, setSelectedValue] = useState(options?.[0]?.value || "");
  const { showToast } = useToast();
  const { globalSettings, setGlobalSettings } = useGlobalSettings();
  const { showLoading, hideLoading } = useLoading();
  const [newPayment, setNewPayment] = useState({
    paymentType: "Cash",
    amount: "",
    paymentDate: new Date().toISOString().slice(0, 16),
    refNumber: "",
    imageUrl: "",
    bankName: "",
    chequeNumber: "",
    remark: "",
  });

  const [cancellationRemark, setCancellationRemark] = useState("");
  // MODIFICATION: Add a useEffect to handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the modal ref exists and the click is outside of its content, call onCancel
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCancel();
      }
    };

    // Add event listener when the modal is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onCancel]); // Dependencies ensure the effect runs only when isOpen or onCancel changes

  useEffect(() => {
    if (isOpen && globalSettings && activePage === "InvoiceSettings") {
      const invoiceSettings =
        globalSettings?.result?.[0]?.invoiceSettings || {};
      const recieptNo = invoiceSettings.recieptNo || {};

      if (!recieptNo.text) {
        showToast(
          "Receipt number text is required. Please configure it in settings.",
          "warning"
        );
      }
    }
  }, [isOpen, globalSettings, showToast, activePage]);

  useEffect(() => {
    // If modal is open and totalAmount is 0, try to fetch fresh invoice data
    const validateTotalAmount = async () => {
      if (
        isOpen &&
        (activePage === "Invoice" || activePage === "systemInvoices") &&
        (!totalAmount || totalAmount === 0) &&
        invoiceId
      ) {
        console.log("⚠️ Total amount is 0, fetching fresh invoice data...");

        try {
          const response = await getInvoicesByID(invoiceId);
          const invoice = response.data.invoice;

          let freshTotal = 0;

          // Try to extract total amount from fresh data
          if (invoice?.totalAmount && Number(invoice.totalAmount) > 0) {
            freshTotal = Number(invoice.totalAmount);
          } else if (invoice?.total && Number(invoice.total) > 0) {
            freshTotal = Number(invoice.total);
          } else if (invoice?.amount && Number(invoice.amount) > 0) {
            freshTotal = Number(invoice.amount);
          } else if (invoice?.items && Array.isArray(invoice.items)) {
            freshTotal = invoice.items.reduce((sum, item) => {
              return sum + (Number(item.amount) || 0);
            }, 0);
          }

          if (freshTotal > 0) {
            console.log("✅ Found fresh total amount:", freshTotal);
            // You could update the parent component here or show the correct amount
            // For now, we'll just log it - the parent should be updated to pass correct totalAmount
          }
        } catch (error) {
          console.error("Error fetching fresh invoice data:", error);
        }
      }
    };

    validateTotalAmount();
  }, [isOpen, totalAmount, invoiceId, activePage]);

  useEffect(() => {
    if (!isOpen) return;

    if (currentStatus) {
      setSelectedValue(currentStatus);
    } else if (options.length > 0) {
      setSelectedValue(options[0].value);
    }

    // Reset new payment form
    setNewPayment({
      paymentType: "Cash",
      amount: "",
      paymentDate: new Date().toISOString().slice(0, 16),
      refNumber: "",
      imageUrl: "",
      bankName: "",
      chequeNumber: "",
      remark: "",
    });

    // ADDED: Reset cancellation remark
    setCancellationRemark("");
  }, [isOpen, currentStatus, options]);

  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened with previousPayments:", previousPayments);
      console.log("Current globalSettings:", globalSettings);
    }
  }, [isOpen, globalSettings, previousPayments]);

  const handleNewPaymentChange = (field, value) => {
    setNewPayment((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUploadComplete = (url) => {
    handleNewPaymentChange("imageUrl", url);
  };
  const generateReceiptId = () => {
    console.log("=== GENERATING RECEIPT ID ===");
    console.log("Current globalSettings:", globalSettings);

    // Extra defensive checks for the structure
    if (!globalSettings) {
      console.warn("⚠️ globalSettings is null/undefined, using defaults");
      return "REC/1";
    }

    if (
      !globalSettings.result ||
      !Array.isArray(globalSettings.result) ||
      globalSettings.result.length === 0
    ) {
      console.warn("⚠️ globalSettings.result is invalid, using defaults");
      return "REC/1";
    }

    if (!globalSettings.result[0]) {
      console.warn("⚠️ globalSettings.result[0] is undefined, using defaults");
      return "REC/1";
    }

    // Safely access nested properties with fallbacks
    const invoiceSettings = globalSettings.result[0].invoiceSettings || {};
    const recieptNo = invoiceSettings.recieptNo || {}; // Changed from predefinedNo to recieptNo
    const currentCount = recieptNo.count || 0; // Changed to use recieptNo.count
    const basePrefix = recieptNo.text || "REC"; // Changed to use recieptNo.text

    console.log("invoiceSettings:", invoiceSettings);
    console.log("recieptNo:", recieptNo); // Changed log message
    console.log("currentCount:", currentCount);
    console.log("basePrefix:", basePrefix);

    // Generate next receipt number
    const nextReceiptNumber = currentCount + 1;
    const receiptId = `${basePrefix}/${nextReceiptNumber}`;

    console.log("Generated receiptId:", receiptId);
    console.log("=== END RECEIPT ID GENERATION ===");

    return receiptId;
  };

  const updateGlobalSettingsCount = () => {
    console.log("=== UPDATING GLOBAL SETTINGS COUNT ===");

    if (!globalSettings) {
      console.warn("⚠️ globalSettings is null/undefined, cannot update count");
      return;
    }

    setGlobalSettings((prevSettings) => {
      if (!prevSettings?.result?.[0]?.invoiceSettings) {
        console.warn(
          "⚠️ Previous settings structure is invalid, cannot update"
        );
        return prevSettings;
      }

      // Only update the specific recieptNo count, preserve everything else
      const updatedSettings = {
        ...prevSettings,
        result: prevSettings.result.map((item, index) => {
          if (index === 0) {
            return {
              ...item,
              invoiceSettings: {
                ...item.invoiceSettings,
                recieptNo: {
                  ...item.invoiceSettings.recieptNo,
                  count: (item.invoiceSettings.recieptNo?.count || 0) + 1,
                },
              },
            };
          }
          return item;
        }),
      };

      console.log("✅ Updated receipt count");
      return updatedSettings;
    });

    console.log("=== END GLOBAL SETTINGS UPDATE ===");
  };

  const handleConfirm = async () => {
    console.log("=== HANDLE CONFIRM STARTED ===");
    console.log("selectedValue:", selectedValue);
    console.log("totalAmount:", totalAmount, "Type:", typeof totalAmount);
    console.log("globalSettings at start:", globalSettings);

    // ADDED: Handle cancellation with remark
    if (selectedValue === "cancelled") {
      console.log(
        "🏁 Calling onConfirm with cancelled status and remark:",
        cancellationRemark
      );
      onConfirm(selectedValue, cancellationRemark);
      return;
    }

    // Validate total amount before processing payments
    const validTotalAmount = Number(totalAmount) || 0;
    if (
      (selectedValue === "paid" || selectedValue === "part-payment") &&
      validTotalAmount <= 0
    ) {
      showToast("Invalid total amount. Cannot process payment.", "error");
      console.error(
        "Invalid total amount for payment processing:",
        totalAmount
      );
      return;
    }

    if (["paid", "part-payment"].includes(selectedValue)) {
      console.log("✅ Entered payment processing section");

      // For paid status with no previous payments, create initial payment
      if (selectedValue === "paid" && previousPayments.length === 0) {
        console.log("🔄 Processing 'paid' status with no previous payments");
        const paidPayment = {
          paymentType: newPayment.paymentType,
          amount: validTotalAmount, // Use validated total amount
          paymentDate: new Date(newPayment.paymentDate).toISOString(),
          refNumber: newPayment.refNumber || "",
          imageUrl: newPayment.imageUrl || "",
          bankName: newPayment.bankName || "",
          chequeNumber: newPayment.chequeNumber || "",
          recieptId: activePage === "systemInvoices" ? "PENDING" : generateReceiptId(),
        };

        console.log("Generated paidPayment:", paidPayment);

        try {
          await submitPaymentDetails(invoiceId, [paidPayment]);
          // Update predefined count only after successful payment (not for system invoices)
          if (activePage !== "systemInvoices") {
            updateGlobalSettingsCount();
          }
          console.log("✅ Paid payment submitted successfully");
        } catch (err) {
          console.error("❌ Payment API failed", err);
          showToast("Failed to submit payment details", "error");
          return; // Don't proceed if payment failed
        }
      }
      // For part-payment, add new payment to existing payments
      else if (selectedValue === "part-payment") {
        console.log("🔄 Processing 'part-payment' status");

        const hasInvalidOtherPayment =
          newPayment.paymentType === "OTHER" &&
          (!newPayment.remark || newPayment.remark.trim() === "");

        if (hasInvalidOtherPayment) {
          console.log("❌ Invalid OTHER payment - missing remark");
          showToast("Please provide remarks for Other payment type", "error");
          return;
        }

        // Validate amount for non-OTHER payments
        if (
          newPayment.paymentType !== "OTHER" &&
          (!newPayment.amount || Number(newPayment.amount) <= 0)
        ) {
          showToast("Please enter a valid payment amount", "error");
          return;
        }

        // Additional validation: Check if payment amount exceeds remaining amount
        if (newPayment.paymentType !== "OTHER" && newPayment.amount) {
          const remainingAmount = Number(getRemainingAmount());
          const paymentAmount = Number(newPayment.amount);

          if (paymentAmount > remainingAmount) {
            showToast(
              `Payment amount (₹${paymentAmount}) cannot exceed remaining amount (₹${remainingAmount})`,
              "error"
            );
            return;
          }
        }

        console.log("✅ Validation passed, formatting payment...");

        const formattedNewPayment = {
          paymentType: newPayment.paymentType,
          paymentDate: new Date(newPayment.paymentDate).toISOString(),
          recieptId: activePage === "systemInvoices" ? "PENDING" : generateReceiptId(),
        };

        if (newPayment.paymentType === "OTHER") {
          if (newPayment.remark) formattedNewPayment.remark = newPayment.remark;
          if (newPayment.amount)
            formattedNewPayment.amount = Number(newPayment.amount);
        } else {
          formattedNewPayment.amount = Number(newPayment.amount);

          if (newPayment.paymentType === "UPI") {
            if (newPayment.refNumber)
              formattedNewPayment.refNumber = newPayment.refNumber;
            if (newPayment.imageUrl)
              formattedNewPayment.imageUrl = newPayment.imageUrl;
          } else if (newPayment.paymentType === "CHEQUE") {
            if (newPayment.bankName)
              formattedNewPayment.bankName = newPayment.bankName;
            if (newPayment.chequeNumber)
              formattedNewPayment.chequeNumber = newPayment.chequeNumber;
            if (newPayment.refNumber)
              formattedNewPayment.refNumber = newPayment.refNumber;
            if (newPayment.imageUrl)
              formattedNewPayment.imageUrl = newPayment.imageUrl;
          }
        }

        console.log("formattedNewPayment:", formattedNewPayment);

        // Handle different possible structures of previousPayments
        let existingPayments = [];

        if (Array.isArray(previousPayments)) {
          existingPayments = previousPayments;
        } else if (
          previousPayments &&
          previousPayments.payments &&
          Array.isArray(previousPayments.payments)
        ) {
          existingPayments = previousPayments.payments;
        } else if (previousPayments && typeof previousPayments === "object") {
          existingPayments = [previousPayments];
        }

        // Combine all payments (existing + new)
        const allPayments = [...existingPayments, formattedNewPayment];

        console.log("allPayments to be sent:", allPayments);

        try {
          console.log("🚀 Submitting payment details...");
          await submitPaymentDetails(invoiceId, allPayments);
          // Update predefined count only after successful payment (not for system invoices)
          if (activePage !== "systemInvoices") {
            updateGlobalSettingsCount();
          }
          console.log("✅ Part payment submitted successfully");
        } catch (err) {
          console.error("❌ Payment API failed", err);
          showToast("Failed to submit payment details", "error");
          return; // Don't proceed if payment failed
        }
      }
    }

    console.log("🏁 Calling onConfirm with selectedValue:", selectedValue);
    onConfirm(selectedValue);
  };

  const getRemainingAmount = () => {
    console.log("Calculating remaining amount:", {
      totalAmount,
      previousPayments,
    });

    // Ensure totalAmount is a valid number
    const validTotalAmount = Number(totalAmount) || 0;

    if (validTotalAmount === 0) {
      console.warn("Total amount is 0 or invalid:", totalAmount);
      return "0.00";
    }

    const totalPaid = previousPayments.reduce((sum, p) => {
      // Only count payments that are not "OTHER" type and have valid amounts
      if (p.paymentType !== "OTHER" && p.amount) {
        return sum + Number(p.amount || 0);
      }
      return sum;
    }, 0);

    const remaining = Math.max(0, validTotalAmount - totalPaid);
    console.log("Remaining calculation:", {
      validTotalAmount,
      totalPaid,
      remaining,
    });

    return remaining.toFixed(2);
  };

  // Updated receipt download function using PDF template
  // const handleDownloadReceipt = async (payment) => {
  //   try {
  //     showLoading("Generating receipt PDF...");
  //     console.log("Starting receipt PDF generation for payment:", payment);

  //     // Get full invoice details
  //     const response = await getInvoicesByID(invoiceId);
  //     const invoice = response.data;
  //     const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;

  //     console.log("Invoice data for receipt:", invoice);
  //     console.log("Settings data for receipt:", invoiceSettings);

  //     const container = document.createElement("div");
  //     container.id = "receipt-pdf-container";
  //     container.style.cssText = `
  //         position: fixed;
  //         top: -9999px;
  //         left: -9999px;
  //         width: 794px;
  //         min-height: auto;
  //         background: white;
  //         padding: 0;
  //         margin: 0;
  //         font-family: Arial, sans-serif;
  //         z-index: 9999;
  //         box-sizing: border-box;
  //         overflow: visible;
  //         display: block;
  //       `;

  //     document.body.appendChild(container);

  //     // Create root and render
  //     const root = ReactDOM.createRoot(container);

  //     // Render the receipt component
  //     root.render(
  //       <div
  //         style={{
  //           width: "794px",
  //           minHeight: "auto",
  //           backgroundColor: "white",
  //           display: "block",
  //           margin: 0,
  //           padding: 0,
  //         }}
  //       >
  //         <ReceiptPDFTemplate
  //           payment={payment}
  //           invoice={invoice}
  //           settings={invoiceSettings}
  //         />
  //       </div>
  //     );

  //     console.log("Receipt component rendered, waiting for content to load...");

  //     // Wait for React to render and calculate actual height
  //     await new Promise((resolve) => setTimeout(resolve, 2000));

  //     // Wait for all images to load
  //     const allImages = container.querySelectorAll("img");
  //     console.log("Found images in receipt:", allImages.length);

  //     if (allImages.length > 0) {
  //       await Promise.all(
  //         Array.from(allImages).map((img, index) => {
  //           return new Promise((resolve) => {
  //             console.log(`Checking receipt image ${index + 1}:`, img.src);

  //             if (img.complete && img.naturalWidth > 0) {
  //               console.log(`Receipt image ${index + 1} already loaded`);
  //               resolve();
  //             } else {
  //               const timeout = setTimeout(() => {
  //                 console.warn(
  //                   `Receipt image ${index + 1} load timeout:`,
  //                   img.src
  //                 );
  //                 resolve();
  //               }, 8000);

  //               img.onload = () => {
  //                 console.log(`Receipt image ${index + 1} loaded successfully`);
  //                 clearTimeout(timeout);
  //                 resolve();
  //               };

  //               img.onerror = () => {
  //                 console.error(
  //                   `Receipt image ${index + 1} failed to load:`,
  //                   img.src
  //                 );
  //                 clearTimeout(timeout);
  //                 resolve();
  //               };
  //             }
  //           });
  //         })
  //       );
  //     }

  //     // Get the actual content height after rendering
  //     const actualHeight = Math.max(
  //       container.scrollHeight,
  //       container.offsetHeight,
  //       800 // Minimum height for receipt
  //     );

  //     // Update container with actual dimensions
  //     container.style.cssText = `
  //         position: fixed;
  //         top: 0;
  //         left: 0;
  //         width: 794px;
  //         height: ${actualHeight}px;
  //         background: white;
  //         padding: 0;
  //         margin: 0;
  //         font-family: Arial, sans-serif;
  //         z-index: 9999;
  //         box-sizing: border-box;
  //         overflow: visible;
  //         display: block;
  //       `;

  //     await new Promise((resolve) => setTimeout(resolve, 1000));

  //     console.log("Receipt content loaded, generating PDF...");

  //     if (!container.innerHTML.trim()) {
  //       throw new Error("Container is empty - no content to convert to PDF");
  //     }

  //     const pdfWidth = 8.27;
  //     const pdfHeight = Math.max(11.69, actualHeight / 96);

  //     const options = {
  //       margin: 0,
  //       filename: `Receipt_${payment.recieptId || "Unknown"}.pdf`,
  //       image: { type: "jpeg", quality: 0.98 },
  //       html2canvas: {
  //         scale: 2,
  //         useCORS: true,
  //         allowTaint: true,
  //         backgroundColor: "#ffffff",
  //         logging: true,
  //         width: 794,
  //         height: actualHeight,
  //         scrollX: 0,
  //         scrollY: 0,
  //         windowWidth: 794,
  //         windowHeight: actualHeight,
  //       },
  //       jsPDF: {
  //         unit: "in",
  //         format: [pdfWidth, pdfHeight],
  //         orientation: "portrait",
  //       },
  //       pagebreak: { mode: ["avoid-all", "css", "legacy"] },
  //     };

  //     const html2pdf = (await import("html2pdf.js")).default;

  //     await html2pdf().set(options).from(container).save();

  //     console.log("Receipt PDF generated successfully!");
  //     showToast("Receipt downloaded successfully!", "success");

  //     // Clean up
  //     setTimeout(() => {
  //       try {
  //         root.unmount();
  //         if (document.body.contains(container)) {
  //           document.body.removeChild(container);
  //         }
  //       } catch (cleanupError) {
  //         console.warn("Receipt cleanup error:", cleanupError);
  //       }
  //     }, 1000);
  //   } catch (error) {
  //     console.error("Error generating receipt PDF:", error);
  //     showToast(`Failed to generate receipt: ${error.message}`, "error");

  //     const container = document.getElementById("receipt-pdf-container");
  //     if (container && document.body.contains(container)) {
  //       try {
  //         document.body.removeChild(container);
  //       } catch (cleanupError) {
  //         console.warn("Receipt cleanup error:", cleanupError);
  //       }
  //     }
  //   } finally {
  //     hideLoading();
  //   }
  // };

  const handleDownloadReceipt = async (payment) => {
    // If external handler is provided (for systemInvoices), use it
    if (externalHandleDownloadReceipt) {
      return externalHandleDownloadReceipt(payment, invoiceId);
    }

    // Otherwise use the internal handler for regular invoices
    try {
      showLoading("Generating receipt PDF...");
      console.log("Starting receipt PDF generation for payment:", payment);

      // Get full invoice details
      const response = await getInvoicesByID(invoiceId);
      const invoice = response.data;
      const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;

      console.log("Invoice data for receipt:", invoice);
      console.log("Settings data for receipt:", invoiceSettings);

      const container = document.createElement("div");
      container.id = "receipt-pdf-container";
      container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 794px;
      min-height: auto;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: 9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      document.body.appendChild(container);

      // Create root and render
      const root = ReactDOM.createRoot(container);

      // Render the receipt component
      root.render(
        <div
          style={{
            width: "794px",
            minHeight: "auto",
            backgroundColor: "white",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        >
          <ReceiptPDFTemplate
            payment={payment}
            invoice={invoice}
            settings={invoiceSettings}
          />
        </div>
      );

      console.log("Receipt component rendered, waiting for content to load...");

      // Wait for React to render and calculate actual height
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Wait for all images to load
      const allImages = container.querySelectorAll("img");
      console.log("Found images in receipt:", allImages.length);

      if (allImages.length > 0) {
        await Promise.all(
          Array.from(allImages).map((img, index) => {
            return new Promise((resolve) => {
              console.log(`Checking receipt image ${index + 1}:`, img.src);

              if (img.complete && img.naturalWidth > 0) {
                console.log(`Receipt image ${index + 1} already loaded`);
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  console.warn(
                    `Receipt image ${index + 1} load timeout:`,
                    img.src
                  );
                  resolve();
                }, 8000);

                img.onload = () => {
                  console.log(`Receipt image ${index + 1} loaded successfully`);
                  clearTimeout(timeout);
                  resolve();
                };

                img.onerror = () => {
                  console.error(
                    `Receipt image ${index + 1} failed to load:`,
                    img.src
                  );
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          })
        );
      }

      // Get the actual content height after rendering
      const actualHeight = Math.max(
        container.scrollHeight,
        container.offsetHeight,
        800 // Minimum height for receipt
      );

      // Update container with actual dimensions - position in viewport but behind everything
      container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 794px;
      height: ${actualHeight}px;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: -9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Receipt content loaded, generating PDF...");

      if (!container.innerHTML.trim()) {
        throw new Error("Container is empty - no content to convert to PDF");
      }

      const pdfWidth = 8.27;
      const pdfHeight = Math.max(11.69, actualHeight / 96);

      const options = {
        margin: 0,
        filename: `Receipt_${payment.recieptId || "Unknown"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
          width: 794,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: actualHeight,
        },
        jsPDF: {
          unit: "in",
          format: [pdfWidth, pdfHeight],
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const html2pdf = (await import("html2pdf.js")).default;

      await html2pdf().set(options).from(container).save();

      console.log("Receipt PDF generated successfully!");
      showToast("Receipt downloaded successfully!", "success");

      // Clean up
      setTimeout(() => {
        try {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn("Receipt cleanup error:", cleanupError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating receipt PDF:", error);
      showToast(`Failed to generate receipt: ${error.message}`, "error");

      const container = document.getElementById("receipt-pdf-container");
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (cleanupError) {
          console.warn("Receipt cleanup error:", cleanupError);
        }
      }
    } finally {
      hideLoading();
    }
  };
  if (!isOpen) return null;
  // REPLACE the return statement in ConfirmationModal.js with this:
  return (
    <div className="confirmation-modal-backdrop">
      <div
        ref={modalRef}
        className={`confirmation-modal ${(activePage === "Invoice" || activePage === "systemInvoices") ? "" : "notinvoice"
          }`}
      >
        <button
          className="branch-form-close-button"
          onClick={onCancel}
          aria-label="Close"
        >
          &times;
        </button>
        <p className={`${(activePage === "Invoice" || activePage === "systemInvoices") ? "" : "notinvoicep"}`}>
          {message}
        </p>

        {/* FIXED: Show status dropdown only for non-cancelled invoices */}
        {(activePage === "Invoice" || activePage === "systemInvoices") &&
          currentStatus !== "paid" &&
          currentStatus !== "cancelled" && (
            <select
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
              className="status-select"
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}

        {(activePage === "Invoice" || activePage === "systemInvoices") &&
          selectedValue === "cancelled" &&
          currentStatus !== "cancelled" && (
            <div className="cancellation-remark-section">
              <label htmlFor="cancellation-remark">
                Cancellation Remark (Optional):
              </label>
              <textarea
                id="cancellation-remark"
                placeholder="Enter reason for cancellation (optional)"
                value={cancellationRemark}
                onChange={(e) => setCancellationRemark(e.target.value)}
                rows="3"
                className="cancellation-remark-textarea"
              />
            </div>
          )}

        {/* FIXED: Show current status for cancelled invoices (read-only) */}
        {(activePage === "Invoice" || activePage === "systemInvoices") && currentStatus === "cancelled" && (
          <div className="status-display cancelled-status">
            <strong>Current Status:</strong>
            <span className="status-badge cancelled">Cancelled</span>
            <p className="status-note">
              This invoice has been cancelled and cannot be modified.
            </p>

            {console.log(cancelledRem, "cancelledRemcancelledRem")}
            {cancelledRem && (
              <div className="cancellation-remark-display">
                <strong>Cancellation Remark:</strong>
                <p className="remark-text">{cancelledRem}</p>
              </div>
            )}

            {/* Show message when no cancellation remark is provided */}
            {cancelledRem === "" && (
              <div className="cancellation-remark-display">
                <strong>Cancellation Remark:</strong>
                <p className="remark-text no-remark">No remark provided</p>
              </div>
            )}
          </div>
        )}

        {/* FIXED: Only show payment section for non-cancelled statuses */}
        {(selectedValue === "paid" || selectedValue === "part-payment") &&
          currentStatus !== "cancelled" && (
            <>
              <div className="payment-section">
                <div className="section-title">Payment Details</div>

                <div className="amount-info">
                  <div>
                    <strong>Total:</strong> ₹
                    {Number(totalAmount) > 0
                      ? Number(totalAmount)
                      : "Loading..."}
                  </div>
                  {selectedValue === "part-payment" && (
                    <div>
                      <strong>Remaining:</strong> ₹
                      {Number(totalAmount) > 0
                        ? getRemainingAmount()
                        : "Loading..."}
                    </div>
                  )}
                  {/* Debug info - remove in production */}
                  {Number(totalAmount) === 0 && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "red",
                        marginTop: "5px",
                      }}
                    >
                      Total amount is 0. This indicates a data loading issue.
                    </div>
                  )}
                </div>

                {/* Show form only for part-payment or paid with no previous payments */}
                {(selectedValue === "part-payment" ||
                  (selectedValue === "paid" &&
                    previousPayments.length === 0)) && (
                    <div className="new-payment-form">
                      <h4>
                        {previousPayments.length > 0
                          ? "Add New Payment"
                          : "Payment Information"}
                      </h4>

                      <div className="payment-row">
                        <div className="pay-form-group">
                          <label>Payment Type</label>
                          <select
                            value={newPayment.paymentType}
                            onChange={(e) =>
                              handleNewPaymentChange(
                                "paymentType",
                                e.target.value
                              )
                            }
                          >
                            <option value="Cash">Cash</option>
                            <option value="UPI">UPI/Netbanking</option>
                            <option value="CHEQUE">Cheque</option>
                            <option value="OTHER">Settlement</option>
                          </select>
                        </div>

                        <div className="pay-form-group">
                          <label>Amount</label>
                          <input
                            type="number"
                            placeholder="Amount"
                            value={
                              selectedValue === "paid" &&
                                previousPayments.length === 0
                                ? totalAmount // Show total amount when paid is selected
                                : newPayment.amount
                            }
                            onChange={(e) =>
                              handleNewPaymentChange("amount", e.target.value)
                            }
                            onWheel={(e) => e.target.blur()}
                            disabled={
                              selectedValue === "paid" &&
                              previousPayments.length === 0
                            }
                          />
                        </div>

                        {newPayment.paymentType !== "OTHER" && (
                          <div className="pay-form-group">
                            <label>Payment Date</label>
                            <input
                              type="datetime-local"
                              value={newPayment.paymentDate}
                              onChange={(e) =>
                                handleNewPaymentChange(
                                  "paymentDate",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        )}

                        {newPayment.paymentType === "UPI" && (
                          <>
                            <div className="pay-form-group">
                              <label>Reference Number</label>
                              <input
                                type="text"
                                placeholder="Transaction Reference"
                                value={newPayment.refNumber}
                                onChange={(e) =>
                                  handleNewPaymentChange(
                                    "refNumber",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="pay-form-group">
                              <FirebaseUploader
                                label="Upload Screenshot"
                                existingUrl={newPayment.imageUrl}
                                onUploadComplete={handleUploadComplete}
                              />
                            </div>
                          </>
                        )}

                        {newPayment.paymentType === "CHEQUE" && (
                          <>
                            <div className="pay-form-group">
                              <label>Bank Name</label>
                              <input
                                type="text"
                                placeholder="Bank Name"
                                value={newPayment.bankName}
                                onChange={(e) =>
                                  handleNewPaymentChange(
                                    "bankName",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="pay-form-group">
                              <label>Cheque Number</label>
                              <input
                                type="text"
                                placeholder="Cheque Number"
                                value={newPayment.chequeNumber}
                                onChange={(e) =>
                                  handleNewPaymentChange(
                                    "chequeNumber",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </>
                        )}

                        {newPayment.paymentType === "OTHER" && (
                          <div className="pay-form-group">
                            <label>Remarks *</label>
                            <input
                              type="text"
                              placeholder="Enter remarks (required)"
                              value={newPayment.remark}
                              onChange={(e) =>
                                handleNewPaymentChange("remark", e.target.value)
                              }
                              required
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Show previous payments table if any exist */}
                {previousPayments.length > 0 && (
                  <div className="previous-payments">
                    <h4>Previous Transactions</h4>
                    <div className="payments-table-container">
                      <table className="payments-table">
                        <thead>
                          <tr>
                            <th>Receipt ID</th>
                            <th>Payment Type</th>
                            <th>Amount</th>
                            <th>Date</th>
                            {/* <th>Username</th> */}
                            {/* <th>Created At</th> */}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previousPayments.map((payment, idx) => (
                            <tr key={idx}>
                              {console.log(
                                payment,
                                "paymentpaymentpaymentpayment"
                              )}
                              <td>{payment.recieptId || "-"}</td>
                              <td>{payment.paymentType}</td>
                              <td>
                                {payment.paymentType !== "OTHER"
                                  ? `₹${payment.amount}`
                                  : "-"}
                              </td>
                              <td>
                                {new Date(
                                  payment.paymentDate
                                ).toLocaleDateString()}
                              </td>
                              {/* <td
                                onClick={() =>
                                  navigate(
                                    `/home/addusers/${payment?.log?.userId}`
                                  )
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {payment?.log?.userName || "--"}
                              </td> */}
                              {/* <td>
                                {formatDateTime(payment?.log?.createdAt) ||
                                  "--"}
                              </td> */}
                              <td>
                                <button
                                  className="download-receipt-btn"
                                  onClick={() => handleDownloadReceipt(payment)}
                                  title="Download Receipt PDF"
                                >
                                  PDF
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

        <div
          className={`confirmation-actions ${(activePage === "Invoice" || activePage === "systemInvoices") ? "payment-confirmation-btn" : "notinvoice"
            }`}
        >
          {/* FIXED: Show Confirm button only for modifiable statuses */}
          {((activePage !== "Invoice" && activePage !== "systemInvoices") ||
            (currentStatus !== "paid" && currentStatus !== "cancelled")) && (
              <button className="confirm-btn" onClick={handleConfirm}>
                Confirm
              </button>
            )}
          <button className="cancel-btn" onClick={onCancel}>
            {(activePage === "Invoice" || activePage === "systemInvoices") &&
              (currentStatus === "paid" || currentStatus === "cancelled")
              ? "Close"
              : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;